import { calculateAbsoluteHumidity, calculateComBoxHumidityPd, calculateMixingRatio } from "@/data/models/ComBoxHumidityUnit";
import { ComBoxSensorData } from "@/data/models/ComBoxSensorData";

export enum WeatherInformationDetailsLevel {
  NONE = "NONE",
  TEMP_AND_HUMIDITY = "TEMP_AND_HUMIDITY",
  ALL = "ALL"
}

export enum DryingGraphYAxis {
  PANEL_TEMPERATURE,
  PANEL_INTENSITY,

  COM_BOX_ABSOLUTE_HUMIDITY,
  COM_BOX_RELATIVE_HUMIDITY,
  COM_BOX_MIXING_RATIO,
  COM_BOX_RESISTANCE_DIGITS,
  COM_BOX_TEMPERATURE,
}

/** Whether the given Y-axis uses the reduced scale (0–20) that requires additional scaling multipliers for power/pressure/precipitation. */
export function isReducedAxis(yAxis: DryingGraphYAxis): boolean {
  return yAxis === DryingGraphYAxis.COM_BOX_ABSOLUTE_HUMIDITY
    || yAxis === DryingGraphYAxis.COM_BOX_MIXING_RATIO;
}

/** Reverse-converts a plotted power percentage value back to Watts. */
export function plotValueToPowerWatts(plotValue: number, yAxis: DryingGraphYAxis): number {
  const scaled = isReducedAxis(yAxis) ? plotValue * 5 : plotValue;
  return (scaled / 100) * 3600;
}

/** Reverse-converts a plotted pressure offset back to absolute hPa. */
export function plotValueToPressureHpa(plotValue: number, yAxis: DryingGraphYAxis): number {
  const scaled = isReducedAxis(yAxis) ? plotValue * 4 : plotValue;
  return scaled + 950;
}

/** Reverse-converts a plotted precipitation value back to mm. Plot values are scaled 5× for visibility. */
export function plotValueToPrecipitationMm(plotValue: number, yAxis: DryingGraphYAxis): number {
  const unscaled = isReducedAxis(yAxis) ? plotValue * 4 : plotValue;
  return unscaled / 5;
}

/** Reverse-converts a plotted shared-axis resistance value back to resistance digits. */
function plotValueToResistanceDigits(plotValue: number, yAxis: DryingGraphYAxis): number {
  return isReducedAxis(yAxis) ? plotValue * 5 : plotValue;
}

/** Returns the humidity unit string for the given right Y-axis type. */
export function getHumidityUnit(yAxis: DryingGraphYAxis): string {
  switch (yAxis) {
    case DryingGraphYAxis.COM_BOX_ABSOLUTE_HUMIDITY:
      return 'g/m³';
    case DryingGraphYAxis.COM_BOX_MIXING_RATIO:
      return 'g/kg';
    case DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS:
      return 'digits';
    default:
      return '%';
  }
}

export function getSelectedHumidityValue(
  yAxis: DryingGraphYAxis,
  relativeHumidity: number,
  temperature: number,
): number | null {
  const pd = calculateComBoxHumidityPd(temperature, relativeHumidity);

  switch (yAxis) {
    case DryingGraphYAxis.COM_BOX_ABSOLUTE_HUMIDITY:
      return calculateAbsoluteHumidity(temperature, pd);
    case DryingGraphYAxis.COM_BOX_RELATIVE_HUMIDITY:
      return relativeHumidity;
    case DryingGraphYAxis.COM_BOX_MIXING_RATIO:
      return calculateMixingRatio(pd);
    default:
      return null;
  }
}

export function getComBoxYAxisValue(
  yAxis: DryingGraphYAxis,
  comBoxSensorData: ComBoxSensorData
): number | null {
  switch (yAxis) {
    case DryingGraphYAxis.COM_BOX_TEMPERATURE:
      return comBoxSensorData?.temperature ?? null;
    case DryingGraphYAxis.COM_BOX_ABSOLUTE_HUMIDITY:
      return comBoxSensorData?.absoluteHumidity ?? null;
    case DryingGraphYAxis.COM_BOX_RELATIVE_HUMIDITY:
      return comBoxSensorData?.relativeHumidity ?? null;
    case DryingGraphYAxis.COM_BOX_MIXING_RATIO:
      return comBoxSensorData?.mixingRatio ?? null;
    case DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS:
      return comBoxSensorData?.resistanceDigits ?? null;
    default:
      return null;
  }
}

export function getYAxisMinMax(yAxis: DryingGraphYAxis) {
  switch (yAxis) {
    case DryingGraphYAxis.COM_BOX_TEMPERATURE:
      return {
        min: 0,
        max: 50,
        stepSize: 10,
      };
    case DryingGraphYAxis.COM_BOX_ABSOLUTE_HUMIDITY:
      return {
        min: 0,
        max: 20,
        stepSize: 4,
      };
    case DryingGraphYAxis.COM_BOX_RELATIVE_HUMIDITY:
      return {
        min: 0,
        max: 100,
        stepSize: 20,
      };
    case DryingGraphYAxis.COM_BOX_MIXING_RATIO:
      return {
        min: 0,
        max: 20,
        stepSize: 4,
      };
    case DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS:
      return {
        min: 0,
        max: 100,
        stepSize: 20,
      };

    case DryingGraphYAxis.PANEL_INTENSITY:
      return {
        min: 0,
        max: 105,
        stepSize: 15,
      };
    case DryingGraphYAxis.PANEL_TEMPERATURE:
      return {
        min: 10,
        max: 80,
        stepSize: 10,
      };
  }
}

export function getYAxisMaxValueString(
  vue: Vue,
  yAxis: DryingGraphYAxis
): string {
  switch (yAxis) {
    case DryingGraphYAxis.COM_BOX_TEMPERATURE:
    case DryingGraphYAxis.PANEL_TEMPERATURE:
      return vue.$t("dryingGraph.temperatureAxisLabel").toString();
    case DryingGraphYAxis.COM_BOX_ABSOLUTE_HUMIDITY:
      return vue.$t("dryingGraph.absoluteHumidityAxisLabel").toString();
    case DryingGraphYAxis.COM_BOX_RELATIVE_HUMIDITY:
      return vue.$t("dryingGraph.relativeHumidityAxisLabel").toString();
    case DryingGraphYAxis.COM_BOX_MIXING_RATIO:
      return vue.$t("dryingGraph.mixingRatioAxisLabel").toString();
    case DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS:
      return vue.$t("dryingGraph.resistanceDigitsAxisLabel").toString();
    case DryingGraphYAxis.PANEL_INTENSITY:
      return vue.$t("iresDeviceSensor.intensity").toString();
  }
}

export function getYAxisFormatString(
  vue: Vue,
  yAxis: DryingGraphYAxis
): string {
  switch (yAxis) {
    case DryingGraphYAxis.COM_BOX_TEMPERATURE:
      return vue.$t("common.formatTemperature").toString();
    case DryingGraphYAxis.COM_BOX_ABSOLUTE_HUMIDITY:
      return vue.$t("comBox.humidityUnit.formatAbsoluteHumidity").toString();
    case DryingGraphYAxis.COM_BOX_RELATIVE_HUMIDITY:
      return vue.$t("comBox.humidityUnit.formatPercent").toString();
    case DryingGraphYAxis.COM_BOX_MIXING_RATIO:
      return vue.$t("comBox.humidityUnit.formatMixingRatio").toString();
    case DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS:
      return vue.$t("dryingGraph.formatResistanceDigits").toString();
    case DryingGraphYAxis.PANEL_INTENSITY:
      return vue.$t("panelDetail.formatHeatRatio").toString();
    case DryingGraphYAxis.PANEL_TEMPERATURE:
      return vue.$t("common.formatTemperature").toString();
  }
}

/** Builds the axis unit line for the given combination of humidity unit, weather, and power. */
function buildAxisUnitLine(
  humidityUnit: string,
  showWeather: boolean,
  showPower: boolean,
): string {
  const parts: string[] = [humidityUnit];
  if (showWeather) {
    parts.push("mm");
    parts.push("hPa");
  }
  if (showPower) parts.push("W");
  return "(" + parts.join(" / ") + ")";
}

/** Appends the resistance axis title in shared-axis mode to mirror humidity header style. */
function withResistanceHeader(
  vue: Vue,
  label: string,
  includeResistanceValue?: boolean,
): string {
  if (!includeResistanceValue) {
    return label;
  }

  const resistanceLabel = vue.$t("dryingGraph.resistanceDigitsAxisLabel").toString();
  if (label.includes(resistanceLabel)) {
    return label;
  }

  return `${label}/${resistanceLabel}`;
}

export function formatYAxisValue(
  vue: Vue,
  yAxis: DryingGraphYAxis,
  value: number,
  maxValue: number,
  isIresClientOrAdmin?: boolean,
  showPowerLine?: boolean,
  weatherInformationDetailsLevel?: WeatherInformationDetailsLevel,
  includeResistanceValue?: boolean,
): string | string[] {
  const showWeather = weatherInformationDetailsLevel === WeatherInformationDetailsLevel.ALL && !!isIresClientOrAdmin;
  const showPower = !!showPowerLine && !!isIresClientOrAdmin;

  if (value === maxValue) {
    switch (yAxis) {
      case DryingGraphYAxis.COM_BOX_TEMPERATURE:
      case DryingGraphYAxis.PANEL_TEMPERATURE:
        return [vue.$t("dryingGraph.temperatureAxisLabel").toString(), "(°C)"];
      case DryingGraphYAxis.COM_BOX_ABSOLUTE_HUMIDITY: {
        let label: string;
        if (includeResistanceValue && showWeather && showPower) {
          label = vue.$t("comBox.humidityUnit.absoluteHumidityAll").toString();
        } else if (includeResistanceValue && showWeather) {
          label = vue.$t("comBox.humidityUnit.absoluteHumidityAllWithoutPower").toString();
        } else if (includeResistanceValue && showPower) {
          label = vue.$t("comBox.humidityUnit.absoluteHumidityAndPower").toString();
        } else if (showWeather && showPower) {
          label = vue.$t("comBox.humidityUnit.absoluteHumidityAll").toString();
        } else if (showPower) {
          label = vue.$t("comBox.humidityUnit.absoluteHumidityAndPower").toString();
        } else if (showWeather) {
          label = vue.$t("comBox.humidityUnit.absoluteHumidityAllWithoutPower").toString();
        } else {
          label = vue.$t("dryingGraph.absoluteHumidityAxisLabel").toString();
        }
        const unitParts = ["g/m³"];
        if (includeResistanceValue) unitParts.push("digits");
        if (showWeather) {
          unitParts.push("mm");
          unitParts.push("hPa");
        }
        if (showPower) unitParts.push("W");
        return [withResistanceHeader(vue, label, includeResistanceValue), "(" + unitParts.join(" / ") + ")"];
      }
      case DryingGraphYAxis.COM_BOX_RELATIVE_HUMIDITY: {
        let label: string;
        if (showWeather && showPower) {
          label = vue.$t("comBox.humidityUnit.relativeHumidityAll").toString();
        } else if (showPower) {
          label = vue.$t("comBox.humidityUnit.relativeHumidityAndPowerConsumption").toString();
        } else if (showWeather) {
          label = vue.$t("comBox.humidityUnit.relativeHumidityAndPressure").toString();
        } else {
          label = vue.$t("dryingGraph.relativeHumidityAxisLabel").toString();
        }
        // Relative humidity and resistance digits share the same 0–100 scale, so the
        // per-tick values coincide and aren't duplicated. The header still lists both
        // so it's clear the axis represents resistance digits too.
        const unitParts = ["%"];
        if (includeResistanceValue) unitParts.push("digits");
        if (showWeather) {
          unitParts.push("mm");
          unitParts.push("hPa");
        }
        if (showPower) unitParts.push("W");
        return [withResistanceHeader(vue, label, includeResistanceValue), "(" + unitParts.join(" / ") + ")"];
      }
      case DryingGraphYAxis.COM_BOX_MIXING_RATIO: {
        let label: string;
        if (includeResistanceValue && showWeather && showPower) {
          label = vue.$t("comBox.humidityUnit.mixingRatioAll").toString();
        } else if (includeResistanceValue && showPower) {
          label = vue.$t("comBox.humidityUnit.mixingRatioAndPower").toString();
        } else if (includeResistanceValue && showWeather) {
          label = vue.$t("comBox.humidityUnit.mixingRatioAllWithoutPower").toString();
        } else if (showWeather && showPower) {
          label = vue.$t("comBox.humidityUnit.mixingRatioAll").toString();
        } else if (showPower) {
          label = vue.$t("comBox.humidityUnit.mixingRatioAndPower").toString();
        } else if (showWeather) {
          label = vue.$t("comBox.humidityUnit.mixingRatioAllWithoutPower").toString();
        } else {
          label = vue.$t("dryingGraph.mixingRatioAxisLabel").toString();
        }
        const unitParts = ["g/kg"];
        if (includeResistanceValue) unitParts.push("digits");
        if (showWeather) {
          unitParts.push("mm");
          unitParts.push("hPa");
        }
        if (showPower) unitParts.push("W");
        return [withResistanceHeader(vue, label, includeResistanceValue), "(" + unitParts.join(" / ") + ")"];
      }
      case DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS:
        return [
          vue.$t("dryingGraph.resistanceDigitsAxisLabel").toString(),
          buildAxisUnitLine("digits", showWeather, showPower),
        ];
      case DryingGraphYAxis.PANEL_INTENSITY: {
        if (!isIresClientOrAdmin) return "";
        let label: string;
        if (showWeather) {
          label = vue.$t("iresDeviceSensor.intensityAll").toString();
        } else {
          label = vue.$t("iresDeviceSensor.intensity").toString();
        }
        const parts = ["%"];
        if (showWeather) {
          parts.push("hPa");
          parts.push("mm");
        }
        return [label, "(" + parts.join(" / ") + ")"];
      }
    }
  } else {
    const formattedValue = value.toLocaleString(undefined, {
      maximumFractionDigits: 1,
    });

    const formattedValueForPower = showPower ? plotValueToPowerWatts(value, yAxis) : 0;
    const formattedValueForPressure = showWeather
      ? plotValueToPressureHpa(value, yAxis)
      : 0;
    const formattedValueForPrecipitation = showWeather
      ? plotValueToPrecipitationMm(value, yAxis) 
      : 0;
    const formattedValueForResistance = includeResistanceValue
      ? plotValueToResistanceDigits(value, yAxis)
      : 0;

    switch (yAxis) {
      case DryingGraphYAxis.COM_BOX_TEMPERATURE:
      case DryingGraphYAxis.PANEL_TEMPERATURE:
        return formattedValue;
      
      case DryingGraphYAxis.COM_BOX_ABSOLUTE_HUMIDITY:
      case DryingGraphYAxis.COM_BOX_MIXING_RATIO: {
        const parts: (string | number)[] = [formattedValue];
        if (isIresClientOrAdmin) {
          if (includeResistanceValue) {
            parts.push(
              formattedValueForResistance.toLocaleString(undefined, {
                maximumFractionDigits: 1,
              })
            );
          }
          if (showWeather) { parts.push(formattedValueForPrecipitation); parts.push(formattedValueForPressure); }
          if (showPower) parts.push(formattedValueForPower);
        }
        return parts.join(" / ");
      }

      case DryingGraphYAxis.COM_BOX_RELATIVE_HUMIDITY: {
        const parts: (string | number)[] = [formattedValue];
        if (isIresClientOrAdmin) {
          if (showWeather) {
            parts.push(formattedValueForPrecipitation);
            parts.push(formattedValueForPressure);
          }
          if (showPower) parts.push(formattedValueForPower);
        }
        return parts.join(" / ");
      }

      case DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS:
      {
        const parts: (string | number)[] = [formattedValue];
        if (isIresClientOrAdmin) {
          if (showWeather) {
            parts.push(formattedValueForPrecipitation);
            parts.push(formattedValueForPressure);
          }
          if (showPower) parts.push(formattedValueForPower);
        }
        return parts.join(" / ");
      }
      
      case DryingGraphYAxis.PANEL_INTENSITY: {
        if (!isIresClientOrAdmin) return "";
        const parts: (string | number)[] = [formattedValue];
        if (showWeather) {
          parts.push(formattedValueForPrecipitation);
          parts.push(formattedValueForPressure);
        }
        return parts.join(" / ");
      }
    }
  }

  return "";
}
